/**
 * Site Dashboard UX helpers for offline / pending final count submission.
 *
 * Future V2: service worker / PWA Background Sync so pending final submissions
 * can retry more reliably when the browser is minimised or closed, subject to
 * browser/device support.
 */

export const PENDING_FINAL_SUBMISSION_COPY = {
  title: 'Count pending submission',
  paragraphs: [
    'Your count has been saved on this device but has not yet reached the manager.',
    'SimplyInventory will continue trying to submit it when the connection is available.',
    'Once your connection is back, press Check progress to refresh this page and update the count status.'
  ],
  secondaryNote:
    'Please do not clear browser data or switch devices until the count has submitted successfully.',
  checkProgressLabel: 'Check progress',
  stayOnPageLabel: 'Stay on this page',
  bannerHeadline: 'Count pending submission',
  bannerDetail:
    'Your count is saved on this device and will send when the connection is available. Press Check progress to refresh this page.'
};

export function isPendingFinalSubmissionStatus(status) {
  return status === 'pending' || status === 'syncing';
}

/**
 * Decide whether the pending-submission popup should open.
 * Avoids re-opening after dismiss for the same queued submission id.
 */
export function shouldOpenPendingFinalSubmissionModal({
  isQueuedPending,
  submissionKey,
  dismissedKey,
  alreadyOpen
}) {
  if (!isQueuedPending || !submissionKey) return false;
  if (alreadyOpen) return false;
  if (dismissedKey && dismissedKey === submissionKey) return false;
  return true;
}

export function pendingFinalSubmissionKey(row) {
  if (!row) return null;
  return row.clientSubmissionId || row.id || null;
}

export function isRecalledFinalSubmissionEditing({ recalledBatchId, editingStatus }) {
  return Boolean(
    recalledBatchId ||
      editingStatus === 'recalled' ||
      editingStatus === 'rejected'
  );
}

/**
 * Pending UI is only eligible after an immediate attempt is confirmed queued.
 * The temporary local-first row written before an online POST is not enough.
 */
export function isPendingFinalSubmissionUiEligible({
  row,
  successModalOpen,
  queueBusy,
  submitting,
  recallStateResolved,
  recalledEditing,
  recalledBatchId,
  browserOnline,
  confirmedSubmissionKey
}) {
  if (successModalOpen || !recallStateResolved) return false;
  if (!row || !isPendingFinalSubmissionStatus(row.status)) return false;

  const rowKey = pendingFinalSubmissionKey(row);
  const confirmedCurrentAttempt = Boolean(
    confirmedSubmissionKey &&
      (confirmedSubmissionKey === rowKey || confirmedSubmissionKey === 'pending')
  );
  const rowRecalledBatchId =
    row && row.payload ? row.payload.recalledBatchId || null : null;
  if (
    recalledEditing &&
    (!recalledBatchId || rowRecalledBatchId !== recalledBatchId)
  ) {
    return false;
  }
  const persistedPendingOutcome = Boolean(
    row.pendingConfirmedAt ||
      row.nextRetryAt ||
      (row.lastError && (row.attemptCount || 0) > 0) ||
      (browserOnline === false &&
        row.status === 'pending' &&
        !row.lastAttemptAt)
  );

  if (confirmedCurrentAttempt) return true;
  if (queueBusy || submitting) return false;
  return persistedPendingOutcome;
}

export function handleCheckPendingFinalProgress() {
  if (typeof window !== 'undefined' && typeof window.location.reload === 'function') {
    window.location.reload();
  }
}
